import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

const TermsAndConditions = ({ isOpen, onClose, onAccept }) => {
  const modalRef = useRef(null);
  
  // Close when clicking outside the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-pri-dark/80 backdrop-blur-sm"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl transform-gpu"
            style={{
              background: 'linear-gradient(135deg, rgba(238,201,217,0.2) 0%, rgba(238,201,217,0.1) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              transform: 'perspective(1000px) rotateX(2deg)',
            }}
          >
            {/* Glass-like highlight effect at the top */}
            <div className="absolute inset-x-0 top-0 h-px bg-white/30"></div>
            <div className="absolute -left-10 -right-10 top-0 h-10 rounded-full blur-md bg-gradient-to-r from-soft-pink/20 to-soft-pink/5"></div>
            
            {/* Header */}
            <div className="relative p-6 border-b border-white/10">
              <h3 className="text-2xl font-bold text-soft-pink">Terms and Conditions</h3>
              <button
                onClick={onClose}
                className="absolute top-6 right-6 text-pri-gray hover:text-soft-pink transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto text-pri-gray text-sm leading-relaxed">
              <h4 className="text-lg font-semibold text-soft-pink mb-4">1. Introduction</h4>
              <p className="mb-4">
                Welcome to Jingleez. By using our platform, you agree to comply with and be bound by the following terms and conditions.
                Please review them carefully before using our services.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">2. Acceptance of Terms</h4>
              <p className="mb-4">
                By creating an account on Jingleez, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                If you do not agree with any part of these terms, you may not use our services.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">3. User Accounts</h4>
              <p className="mb-4">
                Users are responsible for maintaining the confidentiality of their account information, including password, 
                and for all activities that occur under their account. You agree to notify us immediately of any unauthorized use 
                of your account or any other breach of security.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">4. User Conduct</h4>
              <p className="mb-4">
                Users agree not to use the Jingleez platform to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Post or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable.</li>
                <li>Impersonate any person or entity or falsely state or otherwise misrepresent your affiliation with a person or entity.</li>
                <li>Upload or transmit any material that infringes any patent, trademark, trade secret, copyright, or other proprietary rights.</li>
                <li>Interfere with or disrupt the service or servers or networks connected to the service.</li>
                <li>Collect or store personal data about other users without their express consent.</li>
              </ul>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">5. Privacy Policy</h4>
              <p className="mb-4">
                Our Privacy Policy, which is incorporated into these Terms by reference, explains how we collect, use, and protect your information.
                By using Jingleez, you consent to our collection and use of personal data as outlined in the Privacy Policy.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">6. Intellectual Property</h4>
              <p className="mb-4">
                All content, features, and functionality of the Jingleez platform, including but not limited to text, graphics, logos, icons, 
                and software, are the exclusive property of Jingleez and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">7. Limitation of Liability</h4>
              <p className="mb-4">
                In no event shall Jingleez, its officers, directors, employees, or agents, be liable for any indirect, incidental, special, 
                consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, 
                resulting from your access to or use of or inability to access or use the service.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">8. Termination</h4>
              <p className="mb-4">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, 
                including without limitation if you breach the Terms. Upon termination, your right to use the service will immediately cease.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">9. Changes to Terms</h4>
              <p className="mb-4">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, 
                we will try to provide at least 30 days' notice prior to any new terms taking effect.
              </p>
              
              <h4 className="text-lg font-semibold text-soft-pink mb-4">10. Governing Law</h4>
              <p className="mb-8">
                These Terms shall be governed and construed in accordance with the laws, without regard to its conflict of law provisions.
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>
            </div>
            
            {/* Footer/Buttons */}
            <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full border border-white/10 text-pri-white hover:bg-white/5 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={onAccept}
                className="px-4 py-2 rounded-full bg-mint-green text-pri-dark hover:bg-opacity-90 transition-colors flex items-center justify-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                Accept Terms
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TermsAndConditions; 